import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { ElectionData, Candidate } from '../types';
import {
  CheckCircle,
  AlertTriangle,
  Edit3,
  Send,
  X,
  ShieldAlert,
} from 'lucide-react';
import { setSiteMeta } from '../utils/meta';

export const VerifyChoicesPage: React.FC = () => {
  const { voter } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<ElectionData>({});
  const [selections, setSelections] = useState<Record<string, string | string[]>>({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!voter) {
      navigate('/');
      return;
    }

    const currentSync = api.getElectionSettingsSync();
    setSiteMeta({ election: currentSync, pageTitle: 'Verify Ballot Choices' });

    const loadData = async () => {
      const electionData = await api.getElectionData(voter.level);
      setData(electionData);

      // Load choices from localStorage
      const savedSelections: Record<string, string | string[]> = {};
      for (const key of Object.keys(electionData)) {
        const val = localStorage.getItem(`ballot_${key}`);
        if (val) {
          try {
            savedSelections[key] = JSON.parse(val);
          } catch {
            savedSelections[key] = val;
          }
        }
      }
      setSelections(savedSelections);
    };

    loadData();
  }, [voter, navigate]);

  const executeFinalSubmit = async () => {
    if (!voter) return;

    // Check all roles have a selected candidate
    const missing = Object.keys(data).filter((postKey) => {
      const sel = selections[postKey];
      if (!sel) return true;
      if (Array.isArray(sel) && sel.length === 0) return true;
      return false;
    });

    if (missing.length > 0) {
      const missingNames = missing.map((k) => data[k]?.name || k).join(', ');
      setSubmitError(`All positions must be voted on before submission. Missing: ${missingNames}`);
      setShowConfirmModal(false);
      return;
    }

    // Guard against disqualified candidates
    for (const [postKey, choice] of Object.entries(selections)) {
      const choiceIds = Array.isArray(choice) ? choice : [choice];
      for (const cid of choiceIds) {
        const cand = data[postKey]?.candidates.find((c) => c.id === cid);
        if (cand?.isDisqualified) {
          setSubmitError(`Candidate "${cand.name}" has been disqualified mid-election. Please return to ballot to select an active candidate.`);
          setShowConfirmModal(false);
          return;
        }
      }
    }

    try {
      setSubmitting(true);
      setSubmitError('');

      // Build structured selections payload
      const formattedSelections: Record<string, string | string[]> = {};
      Object.entries(selections).forEach(([key, val]) => {
        if (val) formattedSelections[key] = val;
      });

      const res = await api.submitVote({
        matric: voter.matric,
        level: voter.level,
        selections: formattedSelections,
      });

      if (res.success) {
        // Clear local storage ballots
        Object.keys(data).forEach((key) => localStorage.removeItem(`ballot_${key}`));
        sessionStorage.setItem('has_voted_session', 'true');
        navigate('/vote/completed', { state: { voteNumber: res.voteNumber } });
      } else {
        throw new Error(res.message || 'Vote submission failed');
      }
    } catch (e: any) {
      setSubmitError(e.message || 'Error submitting vote. Please try again.');
      setShowConfirmModal(false);
    } finally {
      setSubmitting(false);
    }
  };

  const getCandidateDetails = (postKey: string, candId: string): Candidate | undefined => {
    return data[postKey]?.candidates.find((c) => c.id === candId);
  };

  if (!voter) return null;

  return (
    <div className="dashboard-layout" style={{ minHeight: '100vh', backgroundColor: '#FAFAFA' }}>
      <Sidebar />

      <main className="dashboard-main-content">
        <div style={{ marginBottom: '32px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: '#FF9900', textTransform: 'uppercase', letterSpacing: '1px' }}>
            Final Review
          </div>
          <h1 style={{ fontSize: '32px', fontWeight: 800, color: '#171619', marginTop: '6px' }}>
            Verify Your Choices
          </h1>
          <p style={{ color: '#6B7280', fontSize: '15px', marginTop: '6px' }}>
            Please review your choices before submitting your final vote.
          </p>
        </div>

        {submitError && (
          <div
            style={{
              marginBottom: '24px',
              padding: '16px',
              backgroundColor: '#FEF2F2',
              border: '1.5px solid #EF4444',
              borderRadius: '10px',
              color: '#B91C1C',
              fontSize: '14px',
              fontWeight: 600,
            }}
          >
            *{submitError}
          </div>
        )}

        {/* Warning Alert */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            backgroundColor: '#FFFBEB',
            border: '1.5px solid #F59E0B',
            padding: '16px 20px',
            borderRadius: '12px',
            marginBottom: '32px',
          }}
        >
          <ShieldAlert size={24} color="#D97706" style={{ flexShrink: 0 }} />
          <div style={{ fontSize: '14px', color: '#92400E', lineHeight: 1.5 }}>
            <strong>Important Notice:</strong> Once you submit your vote, it is final and cannot be changed or submitted again. Please make sure your choices are correct.
          </div>
        </div>

        {/* Selected Positions Summary */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {Object.entries(data).map(([postKey, post]) => {
            const rawChoice = selections[postKey];
            const choiceList: string[] = Array.isArray(rawChoice)
              ? rawChoice
              : rawChoice
              ? [rawChoice]
              : [];

            return (
              <div key={postKey} className="cv-post-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                  <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#171619', margin: 0 }}>
                    {post.name}
                  </h2>
                  <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                    Max Choices: {post.max || 1}
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {choiceList.length > 0 ? (
                    choiceList.map((candId) => {
                      const cand = getCandidateDetails(postKey, candId);
                      const isDisq = Boolean(cand?.isDisqualified);

                      return (
                        <div
                          key={candId}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '14px',
                            padding: '12px 14px',
                            backgroundColor: isDisq ? '#FEF2F2' : '#F9FAFB',
                            borderRadius: '10px',
                            border: isDisq ? '1.5px solid #FCA5A5' : '1px solid #F3F4F6',
                          }}
                        >
                          <img
                            src={cand?.image || '/assets/images/indecisive.png'}
                            alt={cand?.name || candId}
                            style={{
                              width: '42px',
                              height: '42px',
                              borderRadius: '8px',
                              objectFit: 'cover',
                              filter: isDisq ? 'grayscale(80%)' : 'none',
                              border: isDisq ? '1.5px solid #FCA5A5' : 'none',
                            }}
                          />
                          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '15px', fontWeight: 700, color: isDisq ? '#991B1B' : '#171619', lineHeight: 1.35 }}>
                                {cand?.name || candId}
                              </span>
                              {isDisq && (
                                <span
                                  style={{
                                    fontSize: '10px',
                                    fontWeight: 800,
                                    color: '#DC2626',
                                    backgroundColor: '#FEE2E2',
                                    border: '1px solid #FCA5A5',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.4px',
                                  }}
                                >
                                  Disqualified
                                </span>
                              )}
                            </div>
                            {cand?.nickname && (
                              <div style={{ color: isDisq ? '#B91C1C' : '#FF9900', fontWeight: 700, fontSize: '11.5px', marginTop: '2px', lineHeight: 1.3 }}>
                                "{cand.nickname}"
                              </div>
                            )}

                            {isDisq ? (
                              <div style={{ fontSize: '11px', color: '#B91C1C', marginTop: '4px', lineHeight: 1.3 }}>
                                ⚠️ <strong>Cannot receive votes:</strong> {cand.disqualificationReason ? cand.disqualificationReason : 'Disqualified mid-election by Electoral Committee.'} Please click "Edit Ballot" to choose an active candidate.
                              </div>
                            ) : (
                              <div style={{ fontSize: '11.5px', color: '#10B981', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
                                <CheckCircle size={13} /> Selected Choice
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ color: '#EF4444', fontSize: '14px', fontStyle: 'italic' }}>
                      No candidate selected
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Action Buttons */}
        <div className="vrc-actions">
          <button
            onClick={() => navigate('/vote/cast-vote')}
            className="btn-secondary"
            style={{ color: '#171619', borderColor: '#D1D5DB' }}
          >
            <Edit3 size={18} />
            <span>Edit Ballot</span>
          </button>

          {(() => {
            const missingPositions = Object.keys(data).filter((postKey) => {
              const sel = selections[postKey];
              if (!sel) return true;
              if (Array.isArray(sel) && sel.length === 0) return true;
              return false;
            });
            const hasIncomplete = missingPositions.length > 0;

            const disqualifiedSelections: string[] = [];
            Object.entries(selections).forEach(([postKey, choice]) => {
              const choiceIds = Array.isArray(choice) ? choice : [choice];
              choiceIds.forEach((cid) => {
                const cand = getCandidateDetails(postKey, String(cid));
                if (cand?.isDisqualified) {
                  disqualifiedSelections.push(cand.name);
                }
              });
            });
            const hasDisqualified = disqualifiedSelections.length > 0;
            const cannotSubmit = hasIncomplete || hasDisqualified;

            return (
              <>
                <button
                  onClick={() => cannotSubmit ? null : setShowConfirmModal(true)}
                  className="btn-primary"
                  disabled={submitting || cannotSubmit}
                  id="btn-confirm-final-ballot"
                  style={{
                    maxWidth: '320px',
                    height: '52px',
                    fontSize: '16px',
                    opacity: cannotSubmit ? 0.5 : 1,
                    cursor: cannotSubmit ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Send size={18} />
                  <span>{submitting ? 'Submitting Ballot...' : 'Confirm & Cast Vote'}</span>
                </button>
                {hasDisqualified && (
                  <div style={{ fontSize: '13px', color: '#DC2626', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', marginTop: '6px', width: '100%', backgroundColor: '#FEF2F2', padding: '10px 14px', borderRadius: '8px', border: '1px solid #FCA5A5' }}>
                    ⚠️ Your ballot contains disqualified candidate(s): {disqualifiedSelections.join(', ')}. Please click "Edit Ballot" to choose active candidates before submitting.
                  </div>
                )}
                {hasIncomplete && !hasDisqualified && (
                  <div style={{ fontSize: '13px', color: '#EF4444', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', width: '100%' }}>
                    You must select a candidate for all {missingPositions.length} remaining position{missingPositions.length > 1 ? 's' : ''} before submitting.
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </main>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(23, 22, 25, 0.75)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '16px',
          }}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              maxWidth: '480px',
              width: '100%',
              padding: '28px 24px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              position: 'relative',
              animation: 'fadeIn 0.2s ease-out',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
          >
            <button
              onClick={() => setShowConfirmModal(false)}
              disabled={submitting}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#9CA3AF',
              }}
            >
              <X size={20} />
            </button>

            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                backgroundColor: '#FEF3C7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '18px',
              }}
            >
              <AlertTriangle size={24} color="#D97706" />
            </div>

            <h3 style={{ fontSize: '20px', fontWeight: 700, color: '#171619', marginBottom: '8px' }}>
              Ready to submit your vote?
            </h3>
            <p style={{ color: '#4B5563', fontSize: '14px', lineHeight: 1.6, marginBottom: '24px' }}>
              Please make sure your choices are correct. Once you submit, your vote is final and cannot be changed.
            </p>

            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                disabled={submitting}
                className="btn-secondary modal-btn-cancel"
                style={{ color: '#4B5563', borderColor: '#E5E7EB', padding: '11px 20px', fontSize: '14px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={executeFinalSubmit}
                disabled={submitting}
                id="btn-seal-vote-now"
                className="btn-primary modal-btn-confirm"
                style={{
                  padding: '11px 24px',
                  fontSize: '14px',
                  backgroundColor: '#171619',
                  color: '#FFFFFF',
                }}
              >
                {submitting ? 'Submitting Vote...' : 'Yes, Submit Vote'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
